import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { formatDateTime, formatMinor } from '../../lib/format';
import {
  Badge,
  Button,
  Card,
  DataTable,
  ErrorBanner,
  FormField,
  Input,
  Modal,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from '../../components/ui';
import { RequirePermission } from '../../lib/auth';
import type { Ticket } from '../../lib/types';

export function OrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [refundOpen, setRefundOpen] = useState(false);
  const [transferTicketId, setTransferTicketId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const orderQuery = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => api.orders.get(orderId!),
    enabled: !!orderId,
  });

  const resendMutation = useMutation({
    mutationFn: () => api.orders.resend(orderId!),
  });

  if (!orderId) return null;
  if (orderQuery.isLoading) return <Spinner />;
  if (orderQuery.error || !orderQuery.data) {
    return (
      <ErrorBanner
        message={orderQuery.error instanceof Error ? orderQuery.error.message : 'Order not found.'}
      />
    );
  }

  const order = orderQuery.data;
  const refundable =
    order.status === 'paid' ||
    order.status === 'fulfilled' ||
    order.status === 'partially_refunded';

  return (
    <div>
      <PageHeader
        title={`Order ${order.id}`}
        description={`${order.buyerEmail} · placed ${formatDateTime(order.createdAt)}`}
        actions={
          <>
            <Badge>{order.status}</Badge>
            <RequirePermission permission="tickets.resend">
              <Button
                variant="secondary"
                onClick={() => resendMutation.mutate()}
                disabled={resendMutation.isPending}
              >
                {resendMutation.isPending ? 'Resending...' : 'Resend tickets'}
              </Button>
            </RequirePermission>
            <RequirePermission permission="orders.refund">
              <Button variant="danger" disabled={!refundable} onClick={() => setRefundOpen(true)}>
                Refund
              </Button>
            </RequirePermission>
            <Button variant="ghost" onClick={() => navigate('/orders')}>
              Back to orders
            </Button>
          </>
        }
      />
      {resendMutation.isError && (
        <ErrorBanner
          message={
            resendMutation.error instanceof Error
              ? resendMutation.error.message
              : 'Could not resend tickets.'
          }
        />
      )}
      {/* The resend request can come back 200 but the actual send still failed (e.g. a live-mode
          tenant with no real email provider key configured) - `notificationStatus` is how the
          server reports that distinct outcome, separate from a thrown/network error above. */}
      {resendMutation.isSuccess && resendMutation.data.notificationStatus === 'failed' && (
        <ErrorBanner
          message={
            resendMutation.data.notificationError ??
            'Tickets could not be resent - the email send failed.'
          }
        />
      )}
      {resendMutation.isSuccess && resendMutation.data.notificationStatus === 'sent' && (
        <Card className="mb-4">Tickets resent to {order.buyerEmail}.</Card>
      )}
      <div className="grid grid-cols-2 gap-4 mb-6 sm:grid-cols-3">
        <Card title="Subtotal">{formatMinor(order.subtotalMinor, order.currency)}</Card>
        {order.discountMinor > 0 && (
          <Card title="Discount">-{formatMinor(order.discountMinor, order.currency)}</Card>
        )}
        <Card title="Fees">{formatMinor(order.feesMinor, order.currency)}</Card>
        <Card title="Total paid">{formatMinor(order.totalMinor, order.currency)}</Card>
        <Card title="Promo code">{order.promoCode ?? 'None'}</Card>
        <Card title="Tickets">{(order.tickets ?? []).length}</Card>
      </div>
      <Card title="Buyer" className="mb-6">
        <p className="text-sm text-slate-700">{order.buyerEmail}</p>
        {order.buyerPhone && <p className="text-sm text-slate-500">{order.buyerPhone}</p>}
      </Card>
      <Card title="Attendees / tickets" className="mb-6">
        <DataTable<Ticket>
          columns={[
            {
              key: 'id',
              header: 'Ticket',
              render: (t) => <span className="font-mono text-xs">{t.id}</span>,
            },
            { key: 'holder', header: 'Holder', render: (t) => t.holderName ?? order.buyerEmail },
            { key: 'status', header: 'Status', render: (t) => <Badge>{t.status}</Badge> },
            {
              key: 'checkedIn',
              header: 'Checked in',
              render: (t) => formatDateTime(t.checkedInAt),
            },
            {
              key: 'actions',
              header: '',
              // Only an 'issued' ticket can be transferred (same rule the server itself enforces,
              // routes/management/checkIn.ts's POST .../tickets/:id/transfer) - hidden for an
              // already-transferred/checked-in/void ticket rather than shown disabled, since there
              // is nothing a staff member can do about those states here.
              render: (t) =>
                t.status === 'issued' ? (
                  <RequirePermission permission="tickets.transfer">
                    <Button variant="ghost" onClick={() => setTransferTicketId(t.id)}>
                      Transfer
                    </Button>
                  </RequirePermission>
                ) : null,
            },
          ]}
          rows={order.tickets ?? []}
          getRowKey={(t) => t.id}
          emptyMessage="No tickets issued on this order yet."
        />
      </Card>
      <Card title="Line items">
        <DataTable
          columns={[
            {
              key: 'id',
              header: 'Item',
              render: (i) => <span className="font-mono text-xs">{i.id}</span>,
            },
            { key: 'qty', header: 'Qty', render: (i) => i.quantity },
            {
              key: 'unit',
              header: 'Unit price',
              render: (i) => formatMinor(i.unitPriceMinor, i.currency),
            },
            {
              key: 'amount',
              header: 'Amount',
              render: (i) => formatMinor(i.amountMinor, i.currency),
            },
          ]}
          rows={order.items ?? []}
          getRowKey={(i) => i.id}
          emptyMessage="No line items."
        />
      </Card>
      <RefundModal
        orderId={order.id}
        maxRefundableMinor={order.totalMinor}
        currency={order.currency}
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        onDone={() => queryClient.invalidateQueries({ queryKey: ['order', orderId] })}
      />
      <TransferTicketModal
        ticketId={transferTicketId}
        open={transferTicketId !== null}
        onClose={() => setTransferTicketId(null)}
        onDone={() => queryClient.invalidateQueries({ queryKey: ['order', orderId] })}
      />
    </div>
  );
}

function TransferTicketModal({
  ticketId,
  open,
  onClose,
  onDone,
}: {
  ticketId: string | null;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [holderName, setHolderName] = useState('');
  const [holderEmail, setHolderEmail] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => api.tickets.transfer(ticketId!, { holderName, holderEmail }),
    onSuccess: () => {
      onDone();
      onClose();
      setHolderName('');
      setHolderEmail('');
    },
    onError: (err) => {
      setFormError(err instanceof Error ? err.message : 'Could not transfer this ticket.');
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Transfer ticket"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !holderName || !holderEmail}
          >
            {mutation.isPending ? 'Transferring...' : 'Confirm transfer'}
          </Button>
        </>
      }
    >
      {formError && <ErrorBanner message={formError} />}
      <FormField label="New holder name" htmlFor="transfer-holder-name">
        <Input id="transfer-holder-name" value={holderName} onChange={(e) => setHolderName(e.target.value)} />
      </FormField>
      <FormField label="New holder email" htmlFor="transfer-holder-email">
        <Input
          id="transfer-holder-email"
          type="email"
          value={holderEmail}
          onChange={(e) => setHolderEmail(e.target.value)}
        />
      </FormField>
    </Modal>
  );
}

function RefundModal({
  orderId,
  maxRefundableMinor,
  currency,
  open,
  onClose,
  onDone,
}: {
  orderId: string;
  maxRefundableMinor: number;
  currency: string;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [mode, setMode] = useState<'full' | 'partial'>('full');
  const [amountMajor, setAmountMajor] = useState(String(maxRefundableMinor / 100));
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // `orders.refund` needs a fresh re-auth token (packages/rbac's REAUTH_REQUIRED_PERMISSIONS) -
  // this used to be handled with a bespoke inline password prompt right here, the only reauth-
  // aware action in the whole app. That's now generalized into lib/api.ts's `request()` itself
  // (catches `details.reauthRequired`, opens the shared GlobalReauthDialog, retries
  // automatically) so EVERY reauth-gated mutation gets this for free, not just this one - this
  // modal no longer needs to know anything about reauth at all.
  const mutation = useMutation({
    mutationFn: () =>
      api.orders.refund(orderId, {
        amountMinor: mode === 'full' ? undefined : Math.round(Number(amountMajor) * 100),
        reason: reason || undefined,
      }),
    onSuccess: () => {
      onDone();
      onClose();
    },
    onError: (err) => {
      setFormError(err instanceof Error ? err.message : 'Could not process the refund.');
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Refund order"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending ? 'Refunding...' : 'Confirm refund'}
          </Button>
        </>
      }
    >
      {formError && <ErrorBanner message={formError} />}
      <FormField label="Refund type">
        <Select value={mode} onChange={(e) => setMode(e.target.value as 'full' | 'partial')}>
          <option value="full">Full refund</option>
          <option value="partial">Partial refund</option>
        </Select>
      </FormField>
      {mode === 'partial' && (
        <FormField
          label={`Amount (${currency})`}
          hint={`Up to ${(maxRefundableMinor / 100).toFixed(2)}`}
        >
          <Input
            type="number"
            min={0}
            max={maxRefundableMinor / 100}
            step="0.01"
            value={amountMajor}
            onChange={(e) => setAmountMajor(e.target.value)}
          />
        </FormField>
      )}
      <FormField label="Reason" hint="Optional, shown in the audit log">
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
      </FormField>
    </Modal>
  );
}
